/**
 * API Service for Quote Management
 * 
 * Integrated with MySQL backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('adminToken');
};

// Helper function to create headers
const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Authentication API
export const authAPI = {
  /**
   * Login to admin panel
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<{token: string, user: object}>}
   */
  async login(username, password) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, password })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }
    
    return data;
  },

  /**
   * Verify current session
   * @returns {Promise<{valid: boolean}>}
   */
  async verifySession() {
    const token = getAuthToken();
    if (!token) {
      return { success: false, valid: false };
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        headers: getHeaders()
      });
      const data = await response.json();
      return { ...data, valid: data.success };
    } catch (error) {
      return { success: false, valid: false };
    }
  },

  /**
   * Logout from admin panel
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: getHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
    }
  }
};

// Quotes API
export const quotesAPI = {
  /**
   * Get all quotes
   * @param {object} filters - Optional filters (status, dateFrom, dateTo)
   * @returns {Promise<Array>}
   */
  async getAll(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_BASE_URL}/quotes?${queryString}`, {
      headers: getHeaders()
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch quotes');
    }
    
    return data.data || [];
  },

  /**
   * Get a single quote by ID
   * @param {number|string} id 
   * @returns {Promise<object>}
   */
  async getById(id) {
    const response = await fetch(`${API_BASE_URL}/quotes/${id}`, {
      headers: getHeaders()
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch quote');
    }
    
    return data.data;
  },

  /**
   * Create a new quote (from public form)
   * @param {object} quoteData 
   * @returns {Promise<object>}
   */
  async create(quoteData) {
    const response = await fetch(`${API_BASE_URL}/quotes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(quoteData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create quote');
    }
    
    return data.data;
  },

  /**
   * Update quote status
   * @param {number|string} id 
   * @param {string} status - 'pending' | 'processed' | 'rejected'
   * @returns {Promise<object>}
   */
  async updateStatus(id, status) {
    const response = await fetch(`${API_BASE_URL}/quotes/${id}/status`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update quote status');
    }
    
    return data.data;
  },

  /**
   * Delete a quote
   * @param {number|string} id 
   * @returns {Promise<void>}
   */
  async delete(id) {
    const response = await fetch(`${API_BASE_URL}/quotes/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete quote');
    }
  },

  /**
   * Get quotes statistics
   * @returns {Promise<object>}
   */
  async getStats() {
    const response = await fetch(`${API_BASE_URL}/quotes/stats/summary`, {
      headers: getHeaders()
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch stats');
    }
    
    return data.data;
  }
};

// Export all APIs
const api = {
  auth: authAPI,
  quotes: quotesAPI,
};

export default api;
